import { generatePath, matchPath } from 'react-router-dom'

export type LegacyRedirectParams = Readonly<Record<string, string | undefined>>

export type LegacyRedirectRule = {
  /**
   * Old route pattern.
   *
   * Examples:
   * /programs/*
   * /request/:requestId
   */
  from: string

  /**
   * New route pattern or a function for complex transformations.
   */
  to: string | ((params: LegacyRedirectParams) => string)

  /**
   * Defaults to false.
   */
  caseSensitive?: boolean
}

/**
 * Redirect rules are evaluated from top to bottom.
 * Put more-specific routes before wildcard routes.
 */
export const legacyRedirects: LegacyRedirectRule[] = [
  /**
   * Prefix migration.
   *
   * /programs
   *   -> /cpm/programs
   *
   * /programs/123
   *   -> /cpm/programs/123
   *
   * /programs/123/details
   *   -> /cpm/programs/123/details
   */
  {
    from: '/programs/*',
    to: '/cpm/programs/*',
  },
  {
    from: '/intake-details/*',
    to: '/cpm/requests/*',
  },
  {
    from: '/concept-details/*',
    to: '/cpm/design-initiatives/*',
  },

  /**
   * Route name changed, but the ID remains the same.
   *
   * /request/123
   *   -> /cpm/requests/123
   */
  // {
  //   from: '/intake-details/:id',
  //   to: '/cpm/requests/:id',
  // },

  /**
   * Route changed from singular to plural for consistency.
   *
   * /cpm/request/123
   *   -> /cpm/requests/123
   */

  /**
   * Dynamic route with additional nested paths.
   *
   * /project-details/123
   *   -> /cpm/projects/123
   *
   * /project-details/123/schedule
   *   -> /cpm/projects/123/schedule
   */
  // {
  //   from: '/project-details/:projectId/*',
  //   to: '/cpm/projects/:projectId/*',
  // },

  /**
   * Another simple dynamic route.
   *
   * /schedule-details/456
   *   -> /cpm/schedules/456
   */
  // {
  //   from: '/schedule-details/:scheduleId',
  //   to: '/cpm/schedules/:scheduleId',
  // },

  /**
   * Function-based redirect for routes where parameter names
   * or the target route structure are different.
   *
   * /old-request/123
   *   -> /cpm/requests/123/overview
   */
  // {
  //   from: '/old-request/:id',
  //   to: (params) => {
  //     const requestId = params.id

  //     if (!requestId) {
  //       return '/cpm/requests'
  //     }

  //     return `/cpm/requests/${encodeURIComponent(requestId)}/overview`
  //   },
  // },
]

/**
 * Finds a legacy redirect matching the current pathname.
 *
 * Returns the new pathname when a rule matches.
 * Returns undefined when no redirect is needed.
 */
export const resolveLegacyRedirect = (pathname: string): string | undefined => {
  for (const redirectRule of legacyRedirects) {
    const routeMatch = matchPath(
      {
        path: redirectRule.from,
        end: true,
        caseSensitive: redirectRule.caseSensitive ?? false,
      },
      pathname,
    )

    if (!routeMatch) {
      continue
    }

    if (typeof redirectRule.to === 'function') {
      return redirectRule.to(routeMatch.params)
    }

    /*
     * generatePath expects null instead of undefined for an
     * unavailable route parameter.
     */
    const routeParams = Object.fromEntries(
      Object.entries(routeMatch.params).map(([key, value]) => [
        key,
        value ?? null,
      ]),
    ) as Record<string, string | null>

    return generatePath(redirectRule.to, routeParams)
  }

  return undefined
}
